import type {
  ObsCardSpec,
  ObsRole,
  ObsUploadResult,
  ObsWorkbookProfile,
} from "@/types/obs";
import { EMPTY_SIZES, type TileLocale, type TileSizes } from "./tiles";
import { FIRST_STEP, type WizardStep } from "./wizard-steps";

/**
 * Where a half-finished OBS report lives between page loads.
 *
 * 🔑 WHY THIS EXISTS AT ALL. The wizard used to be a modal, where the only way
 * out was a button and React state was enough. It is now a page of its own,
 * opened in its own tab — and a page invites everything a modal never did: a
 * reload, a closed tab, a machine that went to sleep, a colleague's link opened
 * over the top. Losing a confirmed column mapping to any of those is losing the
 * one step of this flow that cannot be automated.
 *
 * TWO STORES, BECAUSE ONE CANNOT HOLD BOTH HALVES:
 *  · localStorage keeps the ANSWERS — titles, the chosen sheet, the confirmed
 *    roles, the declared units, the picked charts. Plain JSON, written on every
 *    change, and readable synchronously so the first render already has them.
 *  · IndexedDB keeps the FILES. A `File` is structured-cloneable and survives a
 *    reload intact; `JSON.stringify` turns one into `{}`. Reading it back is
 *    asynchronous, which is the whole reason the wizard has a hydrating state.
 *
 * ⚠️ A RESTORED FILE CANNOT BE PUT BACK INTO ITS `<input type="file">`. The value
 * of a file input is settable only by the user, for obvious reasons. So every
 * step that took a file shows the RESTORED FILE'S NAME beside the input instead
 * of appearing empty — otherwise a perfectly intact draft reads as a lost one and
 * the operator re-picks a file the wizard already has.
 *
 * 🔴 THE DRAFT HOLDS A MINISTRY BUDGET WORKBOOK. It is cleared when the report is
 * published, when the operator discards it, and on sign-out (see `clearAuth`) —
 * and it expires on its own after a week, so a machine that is simply walked away
 * from does not keep national figures in browser storage indefinitely.
 *
 * ⚠️ EVERY OPERATION IS BEST-EFFORT. Storage throws in private windows, under
 * quota pressure, and when a browser blocks it outright. None of that is a reason
 * to break the wizard: a failed write costs the reload-safety, not the session,
 * so failures are swallowed and the in-memory draft carries on.
 */

/** Everything the wizard collects that can be written as JSON. */
export interface ObsDraftData {
  slug: string;
  titleAr: string;
  titleEn: string;
  /** Measured client-side from the picked images; restored so the size hint survives. */
  tileSizes: TileSizes;
  /**
   * Names of the files held in IndexedDB. Kept HERE rather than read from the
   * restored `File` objects so the first synchronous render can already say
   * "your workbook is still attached", before the async read resolves.
   */
  workbookName: string | null;
  tileNames: Record<TileLocale, string | null>;
  profile: ObsWorkbookProfile | null;
  sheetName: string;
  roles: Partial<Record<ObsRole, string>>;
  measuresConfirmed: boolean;
  unitAr: string;
  unitEn: string;
  scale: string;
  scaleUnitAr: string;
  scaleUnitEn: string;
  multiplier: string;
  charts: ObsCardSpec[];
  enRoles: Partial<Record<string, string>>;
  /** The upload verdict, so the publish step can be reloaded onto. */
  uploaded: ObsUploadResult | null;
  /** Set once the report EXISTS server-side, which is what makes cancelling mean something. */
  createdSlug: string | null;
  sentWorkbook: string | null;
  sentTiles: Partial<Record<TileLocale, string>>;
  /** Where to land when the wizard is opened at its bare URL. */
  lastStep: WizardStep;
}

export const EMPTY_DRAFT: ObsDraftData = {
  slug: "",
  titleAr: "",
  titleEn: "",
  tileSizes: EMPTY_SIZES,
  workbookName: null,
  tileNames: { ar: null, en: null },
  profile: null,
  sheetName: "",
  roles: {},
  measuresConfirmed: false,
  unitAr: "بالدينار العراقي",
  unitEn: "In Iraqi Dinars",
  scale: "1000000000000",
  scaleUnitAr: "ترليون دينار",
  scaleUnitEn: "trillion IQD",
  multiplier: "1",
  charts: [],
  enRoles: {},
  uploaded: null,
  createdSlug: null,
  sentWorkbook: null,
  sentTiles: {},
  lastStep: FIRST_STEP,
};

/**
 * Is there anything here worth telling the operator was restored?
 *
 * A draft that only carries the default currency strings is one nobody started.
 */
export function isDraftStarted(draft: ObsDraftData): boolean {
  return Boolean(
    draft.slug ||
    draft.titleAr ||
    draft.titleEn ||
    draft.profile ||
    draft.createdSlug ||
    draft.workbookName,
  );
}

export type DraftFileKey = "workbook" | "tile:ar" | "tile:en";

export const tileFileKey = (locale: TileLocale): DraftFileKey =>
  locale === "ar" ? "tile:ar" : "tile:en";

const STORAGE_KEY = "mof-admin:obs-create-draft:v1";
const DB_NAME = "mof-admin-obs";
const DB_VERSION = 1;
const FILE_STORE = "draft-files";
/** A draft nobody came back to inside a week is abandoned, not paused. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface Envelope {
  savedAt: number;
  data: ObsDraftData;
}

/* -------------------------------------------------------------------------- */
/* The answers — localStorage                                                 */
/* -------------------------------------------------------------------------- */

export function readDraftData(): ObsDraftData | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Envelope;
    if (!parsed?.data || typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      void clearDraft();
      return null;
    }
    // Merged over the defaults so a draft written by an older build — one that
    // predates a field added since — restores instead of being thrown away.
    return { ...EMPTY_DRAFT, ...parsed.data };
  } catch {
    return null;
  }
}

export function writeDraftData(data: ObsDraftData): void {
  try {
    const envelope: Envelope = { savedAt: Date.now(), data };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    /* private window, quota, blocked storage — the session carries on. */
  }
}

/* -------------------------------------------------------------------------- */
/* The files — IndexedDB                                                      */
/* -------------------------------------------------------------------------- */

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(FILE_STORE)) {
        db.createObjectStore(FILE_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    // Another tab is holding an older version open. Rather than wait forever,
    // give up on persistence — the wizard works, it just will not survive F5.
    request.onblocked = () => resolve(null);
  });
}

/**
 * Run one request against the file store and resolve with its result.
 *
 * The callback returns a bare `IDBRequest` rather than `IDBRequest<unknown>`
 * because the type is INVARIANT — its event handlers mention `this`, so
 * `IDBRequest<undefined>` from `delete` is not assignable to a widened one. The
 * result is narrowed by the only caller that reads it.
 */
function runFileStore(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest,
): Promise<unknown> {
  return openDb().then(
    (db) =>
      new Promise<unknown>((resolve) => {
        if (!db) return resolve(null);
        let request: IDBRequest;
        try {
          request = work(
            db.transaction(FILE_STORE, mode).objectStore(FILE_STORE),
          );
        } catch {
          db.close();
          return resolve(null);
        }
        request.onsuccess = () => {
          resolve(request.result ?? null);
          db.close();
        };
        request.onerror = () => {
          resolve(null);
          db.close();
        };
      }),
  );
}

export async function readDraftFile(key: DraftFileKey): Promise<File | null> {
  const stored = await runFileStore("readonly", (store) => store.get(key));
  // Guarded rather than cast: a record written by an older build, or a browser
  // that hands back a Blob, must not surface as a File the upload then rejects.
  return stored instanceof File ? stored : null;
}

export async function writeDraftFile(
  key: DraftFileKey,
  file: File | null,
): Promise<void> {
  await runFileStore("readwrite", (store) =>
    file ? store.put(file, key) : store.delete(key),
  );
}

/** Forget the whole draft — both halves. Used on publish, discard and sign-out. */
export async function clearDraft(): Promise<void> {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see writeDraftData */
  }
  await runFileStore("readwrite", (store) => store.clear());
}
